import { faArrowRight } from '@fortawesome/pro-light-svg-icons';
import { faCircleChevronLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '../../shared/components/button/Button';
import { useHttpClient } from '../../shared/hooks/http-hook';
import CircularSlider from '../components/CircularSlider';
import Influencers from '../components/Influencers';
import Journal from '../components/Journal';
import One2TenTracker from '../components/One2TenTracker';
import TrackerHeading from '../components/TrackerHeading';

function SleepTrackers(props) {
  const { sendRequest } = useHttpClient();
  const { t } = useTranslation();
  const [isSaved, setIsSaved] = useState(false);
  const [date, setDate] = useState(false);
  const [data, setData] = useState(null);
  const [journal, setJournal] = useState(null);
  const [currentTracker, setCurrentTracker] = useState(null);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);

  const getData = useCallback(
    async (requestedDate) => {
      try {
        const response = await sendRequest(
          `${process.env.REACT_APP_BACKEND_URL}/trackers/sleep/${requestedDate}`
        );
        if (response && response.tracker?._id) {
          const { tracker } = response;
          setCurrentTracker({
            id: tracker._id,
            trackerDate: tracker.tracker_date.toString().slice(0, 10),
          });
          const trackerData = JSON.parse(tracker.data);
          setData(trackerData);
          setJournal({
            enhancers: tracker.enhancers,
            inhibitors: tracker.inhibitors,
            journal: tracker.journal,
          });
          setIsSaved(true);
          setStartTime(trackerData.clock_bed);
          setEndTime(trackerData.clock_wake);
        } else {
          setCurrentTracker({
            id: '',
            trackerDate: requestedDate,
          });
          setData({
            clock_bed: '22:00',
            clock_wake: '06:00',
            quality_of_sleep: '',
          });
          setJournal({
            enhancers: [],
            inhibitors: [],
            journal: '',
          });
          setStartTime('22:00');
          setEndTime('06:00');
        }
      } catch (err) {}
    },
    [sendRequest]
  );

  useEffect(() => {
    if (!date) return;
    getData(date);
  }, [date, getData]);

  const saveResponse = (location, stat) => {
    if (location === 'journal') {
      setJournal((prevState) => {
        return {
          ...prevState,
          [stat.id]: stat.value,
        };
      });
    } else {
      setData((prevState) => {
        return {
          ...prevState,
          [stat.id]: stat.value,
        };
      });
    }
  };

  const sendData = async () => {
    let method = 'POST';
    let url = `${process.env.REACT_APP_BACKEND_URL}/trackers/`;
    if (currentTracker.id !== '') {
      method = 'PUT';
      url = `${process.env.REACT_APP_BACKEND_URL}/trackers/${currentTracker.id}`;
    }
    try {
      const response = await sendRequest(
        url,
        method,
        JSON.stringify({
          trackerDate: date,
          type: 'sleep',
          data,
          hidden: false,
          enhancers: journal.enhancers,
          inhibitors: journal.inhibitors,
          journal: journal.journal,
          platform: 'web',
        })
      );
      if (response) {
        setIsSaved(true);
        props.closeTrackers();
      }
    } catch (err) {}
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendData();
  };

  return (
    <div
      id="snm-sleep"
      className="tracker-container"
      role="region"
      aria-label={`${t('T345')} ${t('tracker.region.title')}`}
    >
      <div className="trackers-inner">
        <div className="back-to-main">
          <button
            className="back-btn"
            aria-label={t('A136')}
            title={t('A136')}
            onClick={props.handleBackToMain}
            type="button"
          >
            <FontAwesomeIcon icon={faCircleChevronLeft} />
          </button>
        </div>
        <TrackerHeading title="T021" setDate={setDate} isSaved={isSaved} />

        {date && data && (
          <form className="tracker-form" onSubmit={handleSubmit} noValidate={false}>
            <div className="trackers-body" role="group" aria-label={t('modal.tracker.details')}>
              <fieldset className="ind-tracker no-padding">
                <legend className="title">{t('T001')}</legend>
                <CircularSlider
                  questionid={['clock_bed', 'clock_wake']}
                  startTime={startTime}
                  endTime={endTime}
                  data={data}
                  saveResponse={saveResponse}
                  title={t('T001')}
                />
                <div className="clock" />
              </fieldset>
              <fieldset className="ind-tracker no-padding">
                <legend className="title title-with-border">{t('T003')}</legend>
                <One2TenTracker
                  questionid="quality_of_sleep"
                  data={data}
                  saveResponse={saveResponse}
                  required
                />
              </fieldset>
              <fieldset className="ind-tracker no-padding">
                <legend className="title title-with-border">{t('T006')}</legend>
                <p className="optional-text">{t('T019')}</p>
                <Influencers
                  sleep
                  questionid={['enhancers', 'inhibitors']}
                  data={journal}
                  saveResponse={saveResponse}
                  greenListAriaLabel={t('trackers.influencers.sleepEnhancers')}
                  redListAriaLabel={t('trackers.influencers.sleepInhibitors')}
                />
              </fieldset>
              <div className="ind-tracker" role="group" aria-label={`${t('T345')} - ${t('T018')}`}>
                <Journal
                  placeholder={t('T541')}
                  questionid="journal"
                  data={journal}
                  saveResponse={saveResponse}
                />
              </div>
            </div>
            <div className="buttons">
              <Button type="button" onClick={props.closeTrackers} variant="secondary" color="dark">
                {t('T015')}
              </Button>
              <Button type="submit" icon={{ faIconDefinition: faArrowRight }} iconPosition="right">
                {isSaved ? 'update' : t('T016')}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
export default SleepTrackers;
